import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { RubricLevelBadgeComponent } from '../../../rubric-level-badge/rubric-level-badge.component';
import {
	StrideLogEntry,
	StrideLogPrediction,
	StrideLogSkillContentAvailability,
	StrideLogSkillStartingConfiguration,
	StrideLogStepGroup,
	StrideLogStepKey,
	StrideScoreCounts,
	StrideScoreMath,
} from '@mira-avant/mtlibs';

interface StepMeta {
	label: string;
	color: string;
}

interface AssessmentScoredDetails {
	userText?: string;
	strideScore?: number;
	strideScoreMath?: StrideScoreMath;
	strideScoreReason?: string;
	strideLevelScore?: number;
	strideLevelReason?: string;
	scoreCounts?: StrideScoreCounts;
}

interface StartingConfigurationDetails {
	skills: StrideLogSkillStartingConfiguration[];
}

interface StartingContentSetDetails {
	skills: StrideLogSkillContentAvailability[];
}

const PREDICTION_LABEL: Record<StrideLogPrediction, string> = {
	[StrideLogPrediction.UPGRADE_TEST_LIKELY]: 'May trigger an upgrade test',
	[StrideLogPrediction.DOWNGRADE_LIKELY]: 'May be downgraded',
	[StrideLogPrediction.NO_CHANGE_EXPECTED]: 'No change expected',
};

// Plain-language labels and colors for each decision step, keyed by StrideLogStepKey so the
// content team can visually scan a history and immediately recognize what kind of step occurred.
const STEP_META: Record<StrideLogStepKey, StepMeta> = {
	[StrideLogStepKey.STARTING_CONFIGURATION]: { label: 'Starting Configuration', color: '#722ed1' },
	[StrideLogStepKey.STARTING_CONTENT_SET]: { label: 'Starting Content Set', color: '#9254de' },
	[StrideLogStepKey.SKILL_SELECTED]: { label: 'Skill Rotation', color: 'blue' },
	[StrideLogStepKey.SKILL_SKIPPED_NO_CONTENT]: { label: 'Skill Skipped', color: 'gray' },
	[StrideLogStepKey.TARGET_LEVEL_SELECTED]: { label: 'Level Targeted', color: 'blue' },
	[StrideLogStepKey.UPGRADE_TEST_TRIGGERED]: { label: 'Upgrade Test', color: 'gold' },
	[StrideLogStepKey.ACTIVITY_SELECTED]: { label: 'Activity Selected', color: 'cyan' },
	[StrideLogStepKey.ACTIVITY_FALLBACK_LEVEL_USED]: { label: 'Fallback Level Used', color: 'orange' },
	[StrideLogStepKey.NO_ACTIVITY_AVAILABLE]: { label: 'No Activity Available', color: 'red' },
	[StrideLogStepKey.ASSESSMENT_RECEIVED]: { label: 'Assessment Received', color: 'purple' },
	[StrideLogStepKey.ASSESSMENT_SCORED]: { label: 'Assessment Scored', color: '#2f54eb' },
	[StrideLogStepKey.INSUFFICIENT_HISTORY]: { label: 'Insufficient History', color: 'gray' },
	[StrideLogStepKey.LEVEL_UPGRADED]: { label: 'Level Upgraded', color: 'green' },
	[StrideLogStepKey.LEVEL_DOWNGRADED]: { label: 'Level Downgraded', color: 'red' },
	[StrideLogStepKey.LEVEL_UNCHANGED]: { label: 'Level Unchanged', color: 'gray' },
};

const GROUP_LABEL: Record<StrideLogStepGroup, string> = {
	[StrideLogStepGroup.CONTENT_SELECTION]: 'Content Selection',
	[StrideLogStepGroup.LEVEL_DECISION]: 'Level Decision',
};

@Component({
	selector: 'app-stride-algorithm-report',
	standalone: true,
	imports: [CommonModule, ZorroModule, RubricLevelBadgeComponent],
	templateUrl: './stride-algorithm-report.component.html',
	styleUrl: './stride-algorithm-report.component.scss',
})
export class StrideAlgorithmReportComponent {
	public entries = input<StrideLogEntry[]>([]);

	public groupLabel(group: StrideLogStepGroup): string {
		return GROUP_LABEL[group] ?? group;
	}

	public stepLabel(stepKey: StrideLogStepKey): string {
		return STEP_META[stepKey]?.label ?? stepKey;
	}

	public stepColor(stepKey: StrideLogStepKey): string {
		return STEP_META[stepKey]?.color ?? 'blue';
	}

	public getAssessmentDetails(entry: StrideLogEntry): AssessmentScoredDetails | null {
		if (entry.stepKey !== StrideLogStepKey.ASSESSMENT_SCORED || !entry.details) {
			return null;
		}

		return entry.details as AssessmentScoredDetails;
	}

	public getStartingConfiguration(entry: StrideLogEntry): StrideLogSkillStartingConfiguration[] | null {
		if (entry.stepKey !== StrideLogStepKey.STARTING_CONFIGURATION || !entry.details) {
			return null;
		}

		return (entry.details as unknown as StartingConfigurationDetails).skills ?? null;
	}

	public getStartingContentSet(entry: StrideLogEntry): StrideLogSkillContentAvailability[] | null {
		if (entry.stepKey !== StrideLogStepKey.STARTING_CONTENT_SET || !entry.details) {
			return null;
		}

		return (entry.details as unknown as StartingContentSetDetails).skills ?? null;
	}

	public predictionLabel(prediction: StrideLogPrediction): string {
		return PREDICTION_LABEL[prediction] ?? prediction;
	}
}
